import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Info, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import Img from "@/components/Img";
import { useT, useLoc } from "@/lib/i18n";

const Folder = ({ active, src, label, onClick, testid }) => (
  <button type="button" data-testid={testid} onClick={onClick} className="group w-28 shrink-0 text-left sm:w-40">
    <div className={`aspect-[16/10] overflow-hidden rounded-lg ring-2 transition-shadow ${active ? "ring-[#006CE4]" : "ring-transparent group-hover:ring-slate-300"}`}>
      {src ? <Img src={src} size="sm" alt={label} className="h-full w-full object-cover" /> : <div className="h-full w-full bg-slate-200" />}
    </div>
    <p className={`mt-1.5 truncate text-sm font-semibold sm:text-base ${active ? "text-[#006CE4]" : "text-slate-800"}`}>{label}</p>
  </button>
);

const Lightbox = ({ list, idx, setIdx }) => {
  const cur = list[idx];
  useEffect(() => {
    const k = (e) => {
      if (e.key === "ArrowRight") setIdx((i) => (i + 1) % list.length);
      if (e.key === "ArrowLeft") setIdx((i) => (i - 1 + list.length) % list.length);
    };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [list.length, setIdx]);
  const nav = "absolute top-1/2 -translate-y-1/2 rounded-full bg-white/15 p-2 text-white backdrop-blur hover:bg-white/30";
  return (
    <div className="fixed inset-0 z-[60] flex flex-col bg-black/95" data-testid="gallery-lightbox">
      <div className="flex items-center justify-between px-4 py-3 text-sm text-white/80">
        <span data-testid="gallery-lightbox-caption">{cur.caption}</span>
        <span className="flex items-center gap-4">{idx + 1} / {list.length}
          <button type="button" data-testid="gallery-lightbox-close" onClick={() => setIdx(null)} className="rounded-full p-1.5 hover:bg-white/15"><X className="h-6 w-6" /></button></span>
      </div>
      <div className="relative flex flex-1 items-center justify-center px-2 pb-6 sm:px-16">
        <Img key={cur.src} src={cur.src} size="lg" eager alt={cur.caption} className="max-h-[80vh] max-w-full rounded-md object-contain" />
        {list.length > 1 && <>
          <button type="button" data-testid="gallery-prev" onClick={() => setIdx((idx - 1 + list.length) % list.length)} className={`${nav} left-2 sm:left-4`}><ChevronLeft className="h-7 w-7" /></button>
          <button type="button" data-testid="gallery-next" onClick={() => setIdx((idx + 1) % list.length)} className={`${nav} right-2 sm:right-4`}><ChevronRight className="h-7 w-7" /></button>
        </>}
      </div>
    </div>
  );
};

export default function PhotoGallery({ open, onOpenChange, property: p, rooms = [], onReserve }) {
  const t = useT();
  const loc = useLoc();
  const [folder, setFolder] = useState("all");
  const [idx, setIdx] = useState(null);
  useEffect(() => { if (open) { setFolder("all"); setIdx(null); } }, [open]);
  const roomPhotos = rooms.filter((r) => r.images?.length).map((r) => ({ id: r.id, name: loc(r, "name"), photos: r.images.map((src) => ({ src, caption: loc(r, "name"), room: true })) }));
  const overview = [...(p.images || []).map((src) => ({ src, caption: p.name })), ...roomPhotos.flatMap((r) => r.photos)];
  const list = folder === "all" ? overview : roomPhotos.find((r) => r.id === folder)?.photos || [];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[100dvh] max-h-[100dvh] w-screen max-w-none flex-col gap-0 rounded-none p-0 sm:rounded-none [&>button]:hidden" data-testid="photo-gallery"
        onEscapeKeyDown={(e) => { if (idx !== null) { e.preventDefault(); setIdx(null); } }}>
        <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-3 sm:px-6">
          <span className="w-9" />
          <div className="flex min-w-0 items-center gap-3">
            <DialogTitle className="truncate font-heading text-base font-bold text-slate-900 sm:text-xl">{p.name}</DialogTitle>
            <button type="button" data-testid="gallery-reserve" onClick={() => { onOpenChange(false); onReserve?.(); }} className="shrink-0 rounded-md bg-[#006CE4] px-4 py-2 text-sm font-bold text-white hover:bg-[#0052B4] sm:text-base">{t("Pesan sekarang")}</button>
          </div>
          <button type="button" data-testid="gallery-close" onClick={() => onOpenChange(false)} className="rounded-full p-2 text-slate-600 hover:bg-slate-100"><X className="h-5 w-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-4 py-5 sm:px-6">
          <div className="flex gap-4 overflow-x-auto pb-2 sm:gap-6" data-testid="gallery-folders">
            <Folder testid="gallery-folder-all" active={folder === "all"} src={overview[0]?.src} label={t("Ikhtisar")} onClick={() => setFolder("all")} />
            {roomPhotos.map((r) => <Folder key={r.id} testid={`gallery-folder-${r.id}`} active={folder === r.id} src={r.photos[0].src} label={r.name} onClick={() => setFolder(r.id)} />)}
          </div>
          <div className="mt-6 grid grid-cols-2 gap-2 sm:gap-4 md:grid-cols-3 lg:grid-cols-4" data-testid="gallery-grid">
            {list.map((ph, i) => (
              <button type="button" key={ph.src + i} data-testid={`gallery-photo-${i}`} onClick={() => setIdx(i)} className="group relative aspect-[4/3] overflow-hidden rounded-lg focus-visible:ring-2 focus-visible:ring-[#006CE4]">
                <Img src={ph.src} size="md" alt={ph.caption} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                {ph.room && <span title={ph.caption} className="absolute bottom-2 right-2 rounded bg-white/95 p-1 text-slate-800 shadow"><Info className="h-4 w-4" /></span>}
              </button>
            ))}
          </div>
        </div>
        {idx !== null && list[idx] && <Lightbox list={list} idx={idx} setIdx={setIdx} />}
      </DialogContent>
    </Dialog>
  );
}
